/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / STATS-PERSONAL — Bereich C (S180 Auswertung-Rework, Cut 4)
   =============================================================================
   Operator-PRODUKTIVITÄT über den gesamten operativen Flow je Operator:
     Aufgenommen → Zubereitet (Speisen/Getränke) → Serviert → Kassiert.
   Scope (Jonas): NUR Produktivität — KEIN Lohn / KEINE Zeiterfassung.
   Daten frisch über stats_operator_productivity (phase_138, audit_log). Die
   Attribution wird zentral über den Pre-Request-Hook (phase_139) gestempelt;
   Vollständigkeit hängt an der Operator-Anmeldung. Charts = inline-HTML-Balken
   (kein recharts), Dark-Mode-Token-Farben. Promise.allSettled-Härtung.
============================================================================ */

import { htmlEscape } from '../../utils/helpers.js';
import { fmtCents } from './stats-tagesgeschaeft.js';
import { collapsibleRowsHtml } from './stats-collapse.js';

/* -------------------------------------------------------------------------- */
/* Pure Helfer (Vitest)                                                        */
/* -------------------------------------------------------------------------- */

/** Ø-Bon (Cents) = Umsatz / Bons, gerundet. 0 wenn keine Bons. */
export function avgBonCents(revenueCents, bons) {
  const b = Number(bons) || 0;
  if (b <= 0) return 0;
  return Math.round((Number(revenueCents) || 0) / b);
}

/** Operator-Name-Fallback: operator_name → user_name → name → 'System'. */
export function resolveOperatorName(row) {
  if (!row) return 'System';
  return row.operator_name || row.user_name || row.name || 'System';
}

/**
 * S217 Cut 4 — Prozessdauer (Sekunden) menschenlesbar. Pure (Vitest).
 *   null/NaN → '—'; <60 s → „Xs"; <60 min → „Xm Ys"; sonst „Xh Ym".
 */
export function fmtDuration(sec) {
  if (sec == null) return '—';   // null/undefined → —; echte 0 bleibt „0s"
  const s = Number(sec);
  if (!isFinite(s) || s < 0) return '—';
  const r = Math.round(s);
  if (r < 60) return `${r}s`;
  if (r < 3600) {
    const m = Math.floor(r / 60), rest = r % 60;
    return rest ? `${m}m ${rest}s` : `${m}m`;
  }
  const h = Math.floor(r / 3600), m = Math.round((r % 3600) / 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

/**
 * RPC-Operatoren → normalisierte Zeilen über alle 4 Stufen, absteigend nach
 * kassiertem Umsatz (pct = Anteil am Gesamt-Kassenumsatz). Pure (für Vitest).
 */
export function buildOperatorRows(operators, totalPaidRevenueCents) {
  const list = Array.isArray(operators) ? operators : [];
  const total = Number(totalPaidRevenueCents)
    || list.reduce((s, o) => s + (Number(o.paid_revenue_cents) || 0), 0);
  return list
    .map(o => {
      const paidRev = Number(o.paid_revenue_cents) || 0;
      const paidBons = Number(o.paid_bons) || 0;
      return {
        operator_id: o.operator_id,
        name: resolveOperatorName(o),
        taken_orders: Number(o.taken_orders) || 0,
        taken_items: Number(o.taken_items) || 0,
        taken_revenue: Number(o.taken_revenue_cents) || 0,
        prep_food: Number(o.prep_food) || 0,
        prep_drink: Number(o.prep_drink) || 0,
        served: Number(o.served) || 0,
        paid_bons: paidBons,
        paid_revenue: paidRev,
        avg: (o.avg_bon_cents != null) ? Number(o.avg_bon_cents) : avgBonCents(paidRev, paidBons),
        pct: total > 0 ? Math.round(paidRev / total * 100) : 0
      };
    })
    .sort((a, b) => b.paid_revenue - a.paid_revenue || b.taken_orders - a.taken_orders);
}

/* -------------------------------------------------------------------------- */
/* Render-Helfer (inline-HTML, Dark-Mode-Token-Farben)                         */
/* -------------------------------------------------------------------------- */

function _kpiCard(label, value, sub) {
  return `
    <div class="stat">
      <div class="stat-label">${htmlEscape(label)}</div>
      <div class="stat-value">${value}</div>
      <div class="stat-sub">${sub || '&nbsp;'}</div>
    </div>`;
}

function _operatorList(prod) {
  const rows = buildOperatorRows(prod && prod.operators, prod && prod.revenue_cents_total != null
    ? prod.revenue_cents_total : (prod && prod.paid_revenue_cents_total));
  if (!rows.length) return `<p class="hint">Keine operator-attribuierten Aktionen im Zeitraum.</p>`;
  const max = Math.max(...rows.map(r => r.paid_revenue), 1);
  // S217 Cut 3-Nachzug — auch der Operator-Ranking-Liste das Top-5-Grundmuster geben.
  const rowsHtml = rows.map((r, idx) => {
    const w = Math.round(r.paid_revenue / max * 100);
    const stages = [
      `📝 Aufg. <strong>${r.taken_orders}</strong> (${r.taken_items} Art.)`,
      `👨‍🍳 Zub. <strong>${r.prep_food}</strong>🍔 / <strong>${r.prep_drink}</strong>🥤`,
      `🛎 Serv. <strong>${r.served}</strong>`,
      `💶 Kass. <strong>${r.paid_bons}</strong> · ${fmtCents(r.paid_revenue)} (Ø ${fmtCents(r.avg)})`
    ].join(' &nbsp;·&nbsp; ');
    return `<div style="margin:10px 0;">
      <div style="display:flex;align-items:baseline;gap:10px;">
        <span style="flex:0 0 18px;color:var(--ink-muted);font-variant-numeric:tabular-nums;">${idx + 1}.</span>
        <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:600;">${htmlEscape(r.name)}</span>
        <span style="flex:0 0 auto;white-space:nowrap;color:var(--ink-soft);font-variant-numeric:tabular-nums;">${fmtCents(r.paid_revenue)} · ${r.pct}%</span>
      </div>
      <span style="display:block;height:10px;margin:4px 0 4px 28px;background:var(--bg-soft);border-radius:5px;overflow:hidden;">
        <span style="display:block;height:100%;width:${w}%;background:var(--green);"></span>
      </span>
      <div style="margin-left:28px;color:var(--ink-soft);font-size:.82rem;font-variant-numeric:tabular-nums;">${stages}</div>
    </div>`;
  }).join('');
  return collapsibleRowsHtml('personal-ops', rowsHtml, rows.length);
}

/* -------------------------------------------------------------------------- */
/* S217 Cut 4 — Prozessdauer-KPIs (Gesamt prominent + Kanal-Drilldown)         */
/* -------------------------------------------------------------------------- */

const PROC_CH_LABEL = { 'dine-in': 'Hier Essen', takeaway: 'Takeaway', delivery: 'Lieferung' };
// Aufgeklappte Kanäle im Prozess-Drilldown — Modul-State (überlebt Re-Render).
const _openProcCh = new Set();
// Letztes Prozess-RPC-Ergebnis für den non-destruktiven Drilldown-Toggle.
let _lastProc = null;

/** Balkenzeile je Prozess-Schritt (Median als Balken, Ø + n daneben). */
function _procStepRows(steps, maxMed) {
  const list = Array.isArray(steps) ? steps : [];
  if (!list.length) return `<p class="hint">Keine Prozessdaten im Zeitraum.</p>`;
  const max = maxMed || Math.max(...list.map(s => Number(s.median_sec) || 0), 1);
  return list.map(s => {
    const med = Number(s.median_sec) || 0;
    const w = Math.round(med / max * 100);
    return `<div style="margin:8px 0;">
      <div style="display:flex;align-items:baseline;gap:10px;">
        <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${htmlEscape(s.label)}</span>
        <span style="flex:0 0 auto;white-space:nowrap;color:var(--ink-soft);font-variant-numeric:tabular-nums;">Median <strong>${fmtDuration(s.median_sec)}</strong> · Ø ${fmtDuration(s.avg_sec)} · n ${Number(s.n) || 0}</span>
      </div>
      <span style="display:block;height:8px;margin-top:3px;background:var(--bg-soft);border-radius:4px;overflow:hidden;">
        <span style="display:block;height:100%;width:${w}%;background:var(--green);"></span>
      </span>
    </div>`;
  }).join('');
}

/** Innerer HTML der Prozessdauer-Karte (#personal-proc) — separat re-render-bar. */
function _processSectionInner(proc) {
  if (!proc) return _sectionError('Prozessdauer');
  const overall = Array.isArray(proc.overall) ? proc.overall : [];
  const channels = (proc.channels && typeof proc.channels === 'object') ? proc.channels : {};
  const chHtml = ['dine-in', 'takeaway', 'delivery'].filter(ch => channels[ch]).map(ch => {
    const c = channels[ch];
    const open = _openProcCh.has(ch);
    return `<div class="proc-ch${open ? ' is-open' : ''}">
      <button type="button" class="proc-ch-head" data-procch="${htmlEscape(ch)}" aria-expanded="${open ? 'true' : 'false'}" onclick="statsToggleProcCh(this)">
        <span class="proc-ch-caret">${open ? '▾' : '▸'}</span>
        <strong>${htmlEscape(PROC_CH_LABEL[ch] || ch)}</strong>
        <span class="proc-ch-count">${Number(c.order_count) || 0} Orders</span>
      </button>
      ${open ? `<div class="proc-ch-body">${_procStepRows(c.steps)}</div>` : ''}
    </div>`;
  }).join('');
  return `
    <p class="stat-sub" style="margin:0 0 12px;">Ø + Median je Prozess-Schritt über alle Kanäle. Der Median ist robuster gegen Ausreißer (z. B. offen gelassene Beta-Bestellungen).</p>
    ${overall.length ? _procStepRows(overall) : `<p class="hint">Keine Prozessdaten im Zeitraum.</p>`}
    <h4 style="margin:16px 0 8px;">Nach Kanal</h4>
    ${chHtml || `<p class="hint">Keine Kanal-Daten im Zeitraum.</p>`}`;
}

/** S217 Cut 4 — Kanal-Drilldown auf-/zuklappen (Modul-State). Nur die Prozess-
 *  Karte non-destruktiv neu zeichnen (kein RPC-Reload). */
export function statsToggleProcCh(el) {
  const ch = el && el.dataset ? el.dataset.procch : null;
  if (!ch) return;
  if (_openProcCh.has(ch)) _openProcCh.delete(ch);
  else _openProcCh.add(ch);
  const host = document.getElementById('personal-proc');
  if (host) host.innerHTML = _processSectionInner(_lastProc);
}

/* -------------------------------------------------------------------------- */
/* Daten laden (RPC) + Render                                                  */
/* -------------------------------------------------------------------------- */

function _supa() { return (window.App && window.App.supabase) || null; }

async function _rpc(name, params) {
  const supa = _supa();
  if (!supa) throw new Error('Keine Verbindung.');
  const { data, error } = await supa.rpc(name, params || {});
  if (error) throw error;
  return data;
}

function _sectionError(label) {
  return `<p class="hint" style="text-align:center;padding:18px;">${htmlEscape(label)} derzeit nicht verfügbar.</p>`;
}

/**
 * Lädt + rendert das Personal-Dashboard in `el`. range = {from,to}.
 * Non-destruktiv. allSettled → Sektions-Degradation (auch wenn nur 1 RPC).
 */
export async function renderPersonalInto(el, range) {
  if (!el) return;
  if (!range || !range.from || !range.to) {
    el.innerHTML = `<p class="hint" style="text-align:center;padding:24px;">Zeitraum wählen.</p>`;
    return;
  }
  el.innerHTML = `<p class="hint" style="text-align:center;padding:24px;">Lade Kennzahlen…</p>`;
  const { from, to } = range;
  // S217 Cut 4 — Prozessdauer-RPC dazu; allSettled → eine noch nicht applizierte
  // Migration (stats_process_durations) degradiert nur ihre eigene Sektion.
  const calls = [
    ['stats_operator_productivity', { p_from: from, p_to: to }],
    ['stats_process_durations', { p_from: from, p_to: to }]
  ];
  try {
    const settled = await Promise.allSettled(calls.map(([n, p]) => _rpc(n, p)));
    settled.forEach((s, i) => { if (s.status === 'rejected') console.error('[stats-personal] rpc', calls[i][0], s.reason); });
    const val = (i) => (settled[i].status === 'fulfilled' ? settled[i].value : null);
    _lastProc = val(1);
    el.innerHTML = _renderDashboard(val(0), val(1));
  } catch (e) {
    console.error('[stats-personal] load:', e);
    el.innerHTML = `<p class="hint" style="text-align:center;padding:24px;">Kennzahlen konnten nicht geladen werden: ${htmlEscape(e.message || String(e))}</p>`;
  }
}

function _renderDashboard(prod, proc) {
  const ops = prod ? (Number(prod.operator_count) || 0) : 0;
  const takenT = prod ? (Number(prod.taken_orders_total) || 0) : 0;
  const prepT = prod ? ((Number(prod.prep_food_total) || 0) + (Number(prod.prep_drink_total) || 0)) : 0;
  const revT = prod ? (Number(prod.paid_revenue_cents_total) || 0) : 0;
  const kpis = `
    <div class="stats">
      ${_kpiCard('Aktive Operatoren', prod ? ops.toLocaleString('de-DE') : '—', prod ? 'mit attribuierten Aktionen' : 'Daten fehlen')}
      ${_kpiCard('Aufgenommene Orders', prod ? takenT.toLocaleString('de-DE') : '—', prod ? `${(Number(prod.taken_items_total) || 0)} Artikel` : 'Daten fehlen')}
      ${_kpiCard('Zubereitet', prod ? prepT.toLocaleString('de-DE') : '—', prod ? `${(Number(prod.prep_food_total) || 0)} Speisen · ${(Number(prod.prep_drink_total) || 0)} Getränke` : 'Daten fehlen')}
      ${_kpiCard('Kassierter Umsatz', prod ? fmtCents(revT) : '—', prod ? `${(Number(prod.paid_bons_total) || 0)} Bons` : 'Daten fehlen')}
    </div>`;
  return `
    ${kpis}
    <div class="card" style="margin-top:16px;">
      <h3 style="margin:0 0 4px;">Operator-Produktivität</h3>
      <p class="stat-sub" style="margin:0 0 12px;">Operativer Flow je Operator: Aufgenommen → Zubereitet → Serviert → Kassiert (Balken = kassierter Umsatz).</p>
      ${prod ? _operatorList(prod) : _sectionError('Operator-Produktivität')}
    </div>
    <div class="card" style="margin-top:16px;">
      <h3 style="margin:0 0 4px;">Prozessdauer</h3>
      <div id="personal-proc">${_processSectionInner(proc)}</div>
    </div>
    <p class="hint" style="margin-top:12px;text-align:center;">Nur Produktivität — keine Lohn-/Zeiterfassung. Zuordnung je nach Operator-Anmeldung.</p>`;
}

if (typeof window !== 'undefined') {
  window.statsToggleProcCh = statsToggleProcCh;   // S217 Cut 4 — Kanal-Drilldown-Toggle (CSP)
}
